/**
 * D3.4: Sezione Arrivi e Presenze
 * Dropdown a cascata: Regione → Provincia → Comune
 * KPI cards, composizione ricettiva, tabella indicatori
 */
import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/Card";
import { Badge } from "../ui/Badge";
import { Spinner } from "../ui/Spinner";
import { Autocomplete } from "../ui/Autocomplete";
import { useArriviIndicators, useArriviVariazione, useArriviOptions } from "../../hooks/useArriviPresenze";
import type { ArriviPresenzeIndicator } from "../../types/database";

function formatNum(n: number | null | undefined): string {
  if (n === null || n === undefined) return "-";
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + "M";
  if (n >= 1_000) return (n / 1_000).toFixed(0) + "K";
  return Number.isInteger(n) ? n.toString() : n.toFixed(1);
}

function VarBadge({ value, label }: { value: number | null; label?: string }) {
  if (value === null || value === undefined) return <span className="text-muted-foreground">-</span>;
  const isPositive = value > 0;
  return (
    <Badge variant={isPositive ? "default" : "secondary"} className={`text-xs ${isPositive ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>
      {isPositive ? "+" : ""}{value.toFixed(1)}%{label ? ` ${label}` : ""}
    </Badge>
  );
}

interface AutocompleteOption {
  value: string;
  label: string;
}

interface ArriviPresenzeSectionProps {
  anno: number;
  yearOptions: AutocompleteOption[];
  onAnnoChange: (anno: number) => void;
  initialProvincia?: string | null;
}

export function ArriviPresenzeSection({ anno, yearOptions, onAnnoChange, initialProvincia }: ArriviPresenzeSectionProps) {
  const [regione, setRegione] = useState<string | null>(null);
  const [provincia, setProvincia] = useState<string | null>(initialProvincia || null);
  const [comune, setComune] = useState<string | null>(null);

  // Fetch filter options
  const { data: options } = useArriviOptions(anno || undefined, regione || undefined);

  // Derived dropdown values
  const regioniOptions = useMemo(() =>
    (options?.regioni || []).map((r: string) => ({ value: r, label: r })),
    [options]
  );
  const provinceOptions = useMemo(() => {
    if (!regione || !options?.hierarchy?.[regione]) return [];
    return Object.keys(options.hierarchy[regione]).sort().map(p => ({ value: p, label: p }));
  }, [regione, options]);
  const comuniOptions = useMemo(() => {
    if (!regione || !provincia || !options?.hierarchy?.[regione]?.[provincia]) return [];
    return options.hierarchy[regione][provincia].map((c: string) => ({ value: c, label: c }));
  }, [regione, provincia, options]);

  // Indicators query — requires at least provincia
  const { data: indicators = [], isLoading } = useArriviIndicators(
    comune || undefined,
    provincia || undefined,
    anno,
    !provincia ? regione || undefined : undefined,
  );

  const { data: variazioni = [] } = useArriviVariazione(
    comune || undefined,
    provincia || undefined,
    anno,
    !provincia ? regione || undefined : undefined,
  );

  // Aggregate KPI
  const kpi = useMemo(() => {
    if (indicators.length === 0) return null;
    const totArrivi = indicators.reduce((s: number, i: ArriviPresenzeIndicator) => s + (i.arrivi_totali || 0), 0);
    const totPresenze = indicators.reduce((s: number, i: ArriviPresenzeIndicator) => s + (i.presenze_totali || 0), 0);
    const totEsercizi = indicators.reduce((s: number, i: ArriviPresenzeIndicator) => s + (i.tot_esercizi_num || 0), 0);
    const totLetti = indicators.reduce((s: number, i: ArriviPresenzeIndicator) => s + (i.tot_esercizi_letti || 0), 0);
    const totAlberghi = indicators.reduce((s: number, i: ArriviPresenzeIndicator) => s + (i.tot_alberghi_num || 0), 0);
    const totAlberghiLetti = indicators.reduce((s: number, i: ArriviPresenzeIndicator) => s + (i.tot_alberghi_letti || 0), 0);
    const totExtra = indicators.reduce((s: number, i: ArriviPresenzeIndicator) => s + (i.tot_extra_num || 0), 0);
    const totExtraLetti = indicators.reduce((s: number, i: ArriviPresenzeIndicator) => s + (i.tot_extra_letti || 0), 0);
    const permMedia = totArrivi > 0 ? totPresenze / totArrivi : null;
    const lettiMediEsercizi = totEsercizi > 0 ? totLetti / totEsercizi : null;
    const lettiMediAlberghi = totAlberghi > 0 ? totAlberghiLetti / totAlberghi : null;
    return { totArrivi, totPresenze, totEsercizi, totLetti, totAlberghi, totAlberghiLetti, totExtra, totExtraLetti, permMedia, lettiMediEsercizi, lettiMediAlberghi };
  }, [indicators]);

  const varKpi = variazioni.length > 0 ? variazioni[0] : null;

  return (
    <div className="space-y-6">
      {/* Cascading Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <Autocomplete
          options={regioniOptions}
          value={regione || ""}
          onChange={(val) => {
            setRegione(val || null);
            setProvincia(null);
            setComune(null);
          }}
          placeholder="Seleziona regione..."
          className="min-w-[200px]"
        />

        <Autocomplete
          options={provinceOptions}
          value={provincia || ""}
          onChange={(val) => {
            setProvincia(val || null);
            setComune(null);
          }}
          placeholder={regione ? "Seleziona provincia..." : "Prima seleziona regione"}
          disabled={!regione}
          className="min-w-[180px]"
        />

        <Autocomplete
          options={comuniOptions}
          value={comune || ""}
          onChange={(val) => setComune(val || null)}
          placeholder={provincia ? `Tutti i comuni (${comuniOptions.length})` : "Prima seleziona provincia"}
          disabled={!provincia}
          className="min-w-[180px]"
        />

        {yearOptions.length > 0 && (
          <Autocomplete
            options={yearOptions}
            value={anno ? String(anno) : ""}
            onChange={(val) => onAnnoChange(Number(val) || anno)}
            placeholder="Anno"
            className="w-[100px]"
            clearable={false}
          />
        )}
      </div>

      {/* Loading */}
      {isLoading && (regione || provincia) && (
        <div className="flex justify-center py-12">
          <Spinner size="lg" />
        </div>
      )}

      {/* Content */}
      {!isLoading && kpi && (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            {[
              { label: "Arrivi totali", value: formatNum(kpi.totArrivi), var: varKpi?.var_pct_arrivi },
              { label: "Presenze totali", value: formatNum(kpi.totPresenze), var: varKpi?.var_pct_presenze },
              { label: "Permanenza media", value: kpi.permMedia ? kpi.permMedia.toFixed(1) + " notti" : "-", var: null },
              { label: "Esercizi ricettivi (n.)", value: formatNum(kpi.totEsercizi), var: varKpi?.var_pct_esercizi },
              { label: "Posti letto totali", value: formatNum(kpi.totLetti), var: varKpi?.var_pct_letti },
            ].map((card, i) => (
              <motion.div
                key={card.label}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
              >
                <Card className="h-full">
                  <CardContent className="pt-4 pb-3 h-full flex flex-col">
                    <p className="text-xs text-muted-foreground">{card.label}</p>
                    <p className="text-2xl font-bold mt-1">{card.value}</p>
                    <div className="mt-auto pt-1">
                      {card.var !== null && card.var !== undefined ? (
                        <VarBadge value={card.var} label={`vs ${anno - 1}`} />
                      ) : (
                        <span className="invisible text-xs">-</span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          {/* Composition chart */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Composizione ricettiva</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {[
                  { label: "Numero esercizi ricettivi", alb: kpi.totEsercizi > 0 ? (indicators.reduce((s: number, i: ArriviPresenzeIndicator) => s + i.tot_alberghi_num, 0) / kpi.totEsercizi * 100) : 0 },
                  { label: "Posti letto", alb: kpi.totLetti > 0 ? (indicators.reduce((s: number, i: ArriviPresenzeIndicator) => s + i.tot_alberghi_letti, 0) / kpi.totLetti * 100) : 0 },
                ].map(row => (
                  <div key={row.label}>
                    <p className="text-xs text-muted-foreground mb-1">{row.label}</p>
                    <div className="h-6 rounded-full overflow-hidden flex bg-gray-100">
                      <div
                        className="h-full rounded-l-full flex items-center justify-end px-2 text-[10px] text-white font-medium"
                        style={{ width: `${Math.max(row.alb, 2)}%`, backgroundColor: "#284899" }}
                      >
                        {row.alb > 10 ? `${row.alb.toFixed(0)}% Alberghiero` : ""}
                      </div>
                      <div
                        className="h-full rounded-r-full flex items-center justify-start px-2 text-[10px] text-white font-medium"
                        style={{ width: `${Math.max(100 - row.alb, 2)}%`, backgroundColor: "#e07b39" }}
                      >
                        {100 - row.alb > 10 ? `${(100 - row.alb).toFixed(0)}% Extra-Alb` : ""}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Indicators table */}
          {indicators.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">
                  Indicatori per comune ({indicators.length} risultati)
                </CardTitle>
              </CardHeader>
              <CardContent className="overflow-x-auto max-h-[500px] overflow-y-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b text-muted-foreground sticky top-0 bg-card z-10">
                      <th className="text-left py-2 px-1">Comune</th>
                      <th className="text-right py-2 px-1">Alberghi (n.)</th>
                      <th className="text-right py-2 px-1">Alberghi (letti)</th>
                      <th className="text-right py-2 px-1">Extra-alb. (n.)</th>
                      <th className="text-right py-2 px-1">Extra-alb. (letti)</th>
                      <th className="text-right py-2 px-1">Tot. esercizi</th>
                      <th className="text-right py-2 px-1">Tot. letti</th>
                      <th className="text-right py-2 px-1">Inc. alberghiera %</th>
                      <th className="text-right py-2 px-1">Inc. fascia alta %</th>
                      <th className="text-right py-2 px-1">Letti medi es.</th>
                      <th className="text-right py-2 px-1">Letti medi alb.</th>
                      <th className="text-right py-2 px-1">Arrivi</th>
                      <th className="text-right py-2 px-1">Presenze</th>
                      <th className="text-right py-2 px-1">Perm. media</th>
                    </tr>
                  </thead>
                  <tbody>
                    {indicators.map((ind: ArriviPresenzeIndicator) => (
                      <tr key={`${ind.comune}-${ind.anno}`} className="border-b border-border/30 hover:bg-muted/30">
                        <td className="py-1.5 px-1 font-medium">{ind.comune}</td>
                        <td className="text-right px-1">{ind.tot_alberghi_num}</td>
                        <td className="text-right px-1">{formatNum(ind.tot_alberghi_letti)}</td>
                        <td className="text-right px-1">{ind.tot_extra_num}</td>
                        <td className="text-right px-1">{formatNum(ind.tot_extra_letti)}</td>
                        <td className="text-right px-1">{ind.tot_esercizi_num}</td>
                        <td className="text-right px-1">{formatNum(ind.tot_esercizi_letti)}</td>
                        <td className="text-right px-1">{ind.incidenza_alberghiera_pct !== null ? ind.incidenza_alberghiera_pct.toFixed(1) + "%" : "-"}</td>
                        <td className="text-right px-1">{ind.incidenza_fascia_alta_pct !== null ? ind.incidenza_fascia_alta_pct.toFixed(1) + "%" : "-"}</td>
                        <td className="text-right px-1">{ind.letti_medi_esercizi !== null ? ind.letti_medi_esercizi.toFixed(1) : "-"}</td>
                        <td className="text-right px-1">{ind.letti_medi_alberghi !== null ? ind.letti_medi_alberghi.toFixed(1) : "-"}</td>
                        <td className="text-right px-1">{formatNum(ind.arrivi_totali)}</td>
                        <td className="text-right px-1">{formatNum(ind.presenze_totali)}</td>
                        <td className="text-right px-1">{ind.permanenza_media !== null ? ind.permanenza_media.toFixed(1) : "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}

          {indicators.length > 100 && (
            <p className="text-sm text-muted-foreground text-center">
              {indicators.length} comuni trovati. Seleziona una provincia per visualizzare la tabella.
            </p>
          )}
        </>
      )}

      {/* Empty state */}
      {!isLoading && !kpi && !regione && (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            Seleziona una regione per visualizzare i dati di arrivi e presenze
          </CardContent>
        </Card>
      )}
    </div>
  );
}
